import { ChevronDown, Download, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { redditSearch, type RedditSearchResult } from 'sprites-types/workflow/tools/redditSearch'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { RedditPostItem } from './RedditPostItem'

interface Props {
  nodeExecution: NodeExecution
}

export function RedditSearchDisplay({ nodeExecution }: Props) {
  const [isExpanded, setIsExpanded] = useState(true)

  const schema = getToolResultSchema(redditSearch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const posts = Array.isArray(result[0])
    ? (result as RedditSearchResult[]).flat()
    : (result as RedditSearchResult)

  // Header actions
  const headerActions: ContextAction[] = [
    {
      id: 'export-posts',
      label: 'Export All',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        console.log('Export Reddit posts:', posts)
      },
    },
  ]

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <MessageSquare className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Reddit Posts</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {posts.length} items
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual posts - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {posts.map((post) => (
            <RedditPostItem key={post.id} post={post} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}
